import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotification } from '@/contexts/PushNotificationContext';
import { useToast } from '@/contexts/ToastContext';
import { callService, messageService, profileService } from '@/services/api';
import CallOverlay from '@/components/call/CallOverlay';
import { CallIncomingModal } from '@/components/call/CallIncomingModal';
import type { CallKind, CallPeer, CallSession } from '@/types/call';

export type { CallKind, CallPeer, CallSession } from '@/types/call';

const RING_TIMEOUT_MS = 35_000;

function getIceServers(): RTCIceServer[] {
  const servers: RTCIceServer[] = [
    { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
  ];
  const turnUrl = import.meta.env.VITE_TURN_URL;
  if (turnUrl) {
    servers.push({
      urls: turnUrl,
      username: import.meta.env.VITE_TURN_USERNAME || undefined,
      credential: import.meta.env.VITE_TURN_CREDENTIAL || undefined,
    });
  }
  return servers;
}

/**
 * Unlock audio context on first user gesture. Browsers block audio autoplay
 * until the user has interacted with the page. Call this once on mount.
 */
function useAudioUnlock() {
  useEffect(() => {
    const unlock = () => {
      // Resume any AudioContext
      const AudioContextCtor =
        window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextCtor) {
        const ctx = new AudioContextCtor();
        if (ctx.state === 'suspended') {
          void ctx.resume();
        }
        ctx.close();
      }
      // Play and pause a silent audio to unlock the audio element path
      const silent = new Audio();
      silent.src = '/audio/ringtone.mp3';
      void silent.play().catch(() => {});
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('pointerdown', unlock);
    };
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('pointerdown', unlock);
    };
  }, []);
}

/**
 * Incoming call ringtone + vibration. Plays the MP3 at /audio/ringtone.mp3 on a loop
 * and vibrates the device while an incoming call is waiting.
 * Fails silently if the browser blocks autoplay or the file/vibration is missing.
 */
function useRingtone(active: boolean, callId: string | null | undefined): HTMLAudioElement | null {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    if (!active) {
      audioRef.current = null;
      return;
    }
    let audio: HTMLAudioElement | null = null;
    let vibrateInterval: number | null = null;
    try {
      audio = new Audio('/audio/ringtone.mp3');
      audio.loop = true;
      audio.volume = 0.7;
      void audio.play().catch(() => {
        // Autoplay blocked; user needs to click the modal to hear sound.
      });

      // Vibrate on loop: 1s vibration, 1s pause, repeat.
      navigator.vibrate?.([1000, 1000]);
      vibrateInterval = window.setInterval(() => {
        navigator.vibrate?.([1000, 1000]);
      }, 2000);
    } catch {
      // Audio/vibration not supported; visual ring is enough.
    }
    audioRef.current = audio;
    return () => {
      if (audio) {
        audio.pause();
        audio.src = '';
        audio.load();
      }
      if (vibrateInterval) window.clearInterval(vibrateInterval);
      navigator.vibrate?.([]); // stop any ongoing vibration
      audioRef.current = null;
    };
  }, [active, callId]);

  return audioRef.current;
}

interface CallContextValue {
  startCall: (peer: CallPeer, kind: CallKind) => Promise<void>;
  acceptCall: () => Promise<void>;
  declineCall: () => void;
  endCall: () => void;
  toggleMute: () => void;
  toggleCamera: () => void;
  toggleCameraDevice: () => void;
  session: CallSession | null;
  showFullScreen: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  muted: boolean;
  cameraOff: boolean;
  cameraDevice: 'user' | 'environment' | null;
  hasCamera: boolean;
  elapsed: number;
}

const CallContext = createContext<CallContextValue | undefined>(undefined);

export function CallProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { register } = usePushNotification();

  // Unlock audio on first user gesture so ringtones and notification sounds work.
  useAudioUnlock();

  const [session, setSession] = useState<CallSession | null>(null);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [cameraDevice, setCameraDevice] = useState<'user' | 'environment' | null>('user');
  const [hasCamera, setHasCamera] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const sessionRef = useRef<CallSession | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const channelReadyRef = useRef(false);
  const signalQueueRef = useRef<Record<string, any>[]>([]);
  const pendingIceRef = useRef<RTCIceCandidateInit[]>([]);
  const remoteDescriptionSetRef = useRef(false);
  const offerSentRef = useRef(false);
  const acceptedRef = useRef(false);
  const incomingRef = useRef<{ id: string; kind: CallKind } | null>(null);
  const cancelledCallsRef = useRef<Set<string>>(new Set());
  const ringTimeoutRef = useRef<number | null>(null);
  const endTimerRef = useRef<number | null>(null);
  const elapsedTimerRef = useRef<number | null>(null);

  // Detect available camera devices.
  const checkCameras = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setHasCamera(false);
      setCameraDevice(null);
      return;
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      setHasCamera(videoDevices.length > 0);
      if (videoDevices.length === 0) {
        setCameraDevice(null);
      } else if (!cameraDevice) {
        setCameraDevice('user');
      }
    } catch {
      setHasCamera(false);
    }
  }, [cameraDevice]);

  useEffect(() => {
    void checkCameras();
  }, [checkCameras]);

  const applySession = useCallback((next: CallSession | null) => {
    sessionRef.current = next;
    setSession(next);
  }, []);

  const patchSession = useCallback((patch: Partial<CallSession>) => {
    const current = sessionRef.current;
    if (!current) return;
    const next = { ...current, ...patch };
    sessionRef.current = next;
    setSession(next);
  }, []);

  const cleanup = useCallback(() => {
    if (ringTimeoutRef.current) {
      window.clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    if (endTimerRef.current) {
      window.clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      window.clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    channelReadyRef.current = false;
    signalQueueRef.current = [];
    pendingIceRef.current = [];
    remoteDescriptionSetRef.current = false;
    offerSentRef.current = false;
    acceptedRef.current = false;
    incomingRef.current = null;

    if (peerConnectionRef.current) {
      try {
        peerConnectionRef.current.close();
      } catch {
        // already closed
      }
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          // already stopped
        }
      });
      localStreamRef.current = null;
    }

    setLocalStream(null);
    setRemoteStream(null);
    setMuted(false);
    setCameraOff(false);
    setElapsed(0);
    setShowFullScreen(false);
  }, []);

  const finishCall = useCallback(
    (status: 'ended' | 'error', message?: string) => {
      const current = sessionRef.current;
      if (!current || current.status === 'ended') return;
      const callId = current.id;

      if (endTimerRef.current) window.clearTimeout(endTimerRef.current);
      patchSession({ status, error: message });
      endTimerRef.current = window.setTimeout(
        () => {
          if (sessionRef.current?.id !== callId) return;
          cleanup();
          applySession(null);
        },
        status === 'error' ? 4000 : 1500,
      );
    },
    [applySession, cleanup, patchSession],
  );

  const sendSignal = useCallback((payload: Record<string, any>) => {
    const channel = channelRef.current;
    if (!channel) return;
    if (!channelReadyRef.current) {
      signalQueueRef.current.push(payload);
      return;
    }
    void channel.send({ type: 'broadcast', event: 'signal', payload });
  }, []);

  const handleSignalRef = useRef<(payload: any) => Promise<void>>(async () => {});

  const setupChannel = useCallback((callId: string) => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    channelReadyRef.current = false;
    signalQueueRef.current = [];

    const channel = supabase.channel(`call:${callId}`, {
      config: { broadcast: { self: false } },
    });
    channel.on('broadcast', { event: 'signal' }, ({ payload }: any) => {
      void handleSignalRef.current(payload);
    });
    channel.subscribe((status) => {
      if (status !== 'SUBSCRIBED') return;
      channelReadyRef.current = true;
      const queued = signalQueueRef.current;
      signalQueueRef.current = [];
      queued.forEach((payload) => {
        void channel.send({ type: 'broadcast', event: 'signal', payload });
      });
      // Lets the caller resend its offer if we subscribed after it was sent.
      void channel.send({
        type: 'broadcast',
        event: 'signal',
        payload: { type: 'ready' },
      });
    });
    channelRef.current = channel;
  }, []);

  const flushIce = useCallback(async () => {
    const pc = peerConnectionRef.current;
    if (!pc) return;
    const queued = pendingIceRef.current;
    pendingIceRef.current = [];
    for (const candidate of queued) {
      try {
        await pc.addIceCandidate(candidate);
      } catch {
        // stale candidate
      }
    }
  }, []);

  const createPeerConnection = useCallback(
    (stream: MediaStream) => {
      if (typeof RTCPeerConnection === 'undefined') {
        throw new Error('This browser does not support WebRTC calls.');
      }
      const pc = new RTCPeerConnection({ iceServers: getIceServers() });

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendSignal({ type: 'ice', candidate: event.candidate.toJSON() });
        }
      };

      pc.ontrack = (event) => {
        if (event.streams[0]) {
          setRemoteStream(event.streams[0]);
          return;
        }
        setRemoteStream((previous) => {
          const next = previous ?? new MediaStream();
          next.addTrack(event.track);
          return next;
        });
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') {
          patchSession({
            status: 'active',
            startedAt: sessionRef.current?.startedAt ?? Date.now(),
          });
        } else if (pc.connectionState === 'failed') {
          toast('Call failed', 'The connection could not be established.', 'destructive');
          // Close the row so the chat still gets a call record.
          const callId = sessionRef.current?.id;
          if (callId) void callService.updateStatus(callId, 'ended');
          finishCall('error', 'Connection failed');
        }
      };

      peerConnectionRef.current = pc;
      return pc;
    },
    [finishCall, patchSession, sendSignal, toast],
  );

  const getLocalMedia = useCallback(
    async (kind: CallKind, cameraOn: boolean): Promise<MediaStream> => {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          'Calling needs a secure (HTTPS) connection and a browser with WebRTC support.',
        );
      }

      const constraints: MediaStreamConstraints = {
        audio: true,
      };

      if (cameraOn) {
        // `facingMode` is a preference, not a device id. Passing 'user' as an
        // exact deviceId made getUserMedia fail with OverconstrainedError and
        // silently fall back to audio only, even when permission was granted.
        constraints.video = { facingMode: 'user' };
      }

      try {
        return await navigator.mediaDevices.getUserMedia(constraints);
      } catch (error: any) {
        const cameraMissing =
          cameraOn &&
          ['NotFoundError', 'NotReadableError', 'OverconstrainedError'].includes(
            error?.name,
          );
        if (!cameraMissing) throw error;
        // Retry without any video constraint before giving up on video.
        try {
          return await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: true,
          });
        } catch {
          toast('Camera unavailable', 'Continuing with audio only.');
          return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        }
      }
    },
    [toast],
  );

  const prepare = useCallback(
    async (kind: CallKind, cameraOn: boolean) => {
      if (typeof RTCPeerConnection === 'undefined') {
        throw new Error('This browser does not support WebRTC calls.');
      }
      const stream = await getLocalMedia(kind, cameraOn);
      localStreamRef.current = stream;
      setLocalStream(stream);
      createPeerConnection(stream);
      // Permissions were just granted, so device labels/counts are now visible.
      void checkCameras();
      return stream;
    },
    [checkCameras, createPeerConnection, getLocalMedia],
  );

  const maybeSendOffer = useCallback(async () => {
    if (!acceptedRef.current || offerSentRef.current) return;
    const pc = peerConnectionRef.current;
    if (!pc) return;
    offerSentRef.current = true;
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendSignal({ type: 'offer', sdp: pc.localDescription });
    } catch (error: any) {
      toast('Call failed', error?.message || 'Could not start the call.', 'destructive');
      finishCall('error', 'Could not start the call');
    }
  }, [finishCall, sendSignal, toast]);

  /**
   * Renegotiate after changing tracks mid-call (e.g. turning the camera on for
   * an audio call). replaceTrack() does not need this, but adding a brand new
   * video track does.
   */
  const renegotiate = useCallback(async () => {
    const pc = peerConnectionRef.current;
    if (!pc) return;
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendSignal({ type: 'offer', sdp: pc.localDescription });
    } catch (error: any) {
      toast('Call error', error?.message || 'Could not update the call.', 'destructive');
    }
  }, [sendSignal, toast]);

  const handleSignal = useCallback(
    async (payload: any) => {
      if (!sessionRef.current) return;
      const pc = peerConnectionRef.current;
      try {
        switch (payload?.type) {
          case 'offer': {
            if (!pc) return;
            // Handle both the initial offer and later renegotiations (for
            // example when the other side turns their camera on mid-call).
            if (pc.signalingState === 'have-local-offer') {
              await pc.setLocalDescription({ type: 'rollback' } as RTCSessionDescriptionInit);
            }
            await pc.setRemoteDescription(payload.sdp);
            remoteDescriptionSetRef.current = true;
            await flushIce();
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            sendSignal({ type: 'answer', sdp: pc.localDescription });
            break;
          }
          case 'answer': {
            if (!pc || pc.signalingState !== 'have-local-offer') return;
            await pc.setRemoteDescription(payload.sdp);
            remoteDescriptionSetRef.current = true;
            await flushIce();
            break;
          }
          case 'ice': {
            if (!payload.candidate) break;
            if (!pc || !remoteDescriptionSetRef.current) {
              pendingIceRef.current.push(payload.candidate);
              break;
            }
            await pc.addIceCandidate(payload.candidate);
            break;
          }
          case 'ready': {
            if (
              sessionRef.current?.direction === 'outgoing' &&
              offerSentRef.current &&
              pc?.localDescription
            ) {
              sendSignal({ type: 'offer', sdp: pc.localDescription });
            }
            break;
          }
          default:
            break;
        }
      } catch (error: any) {
        toast('Call error', error?.message || 'Signaling failed.', 'destructive');
      }
    },
    [flushIce, sendSignal, toast],
  );

  useEffect(() => {
    handleSignalRef.current = handleSignal;
  }, [handleSignal]);

  const startCall = useCallback(
    async (peer: CallPeer, kind: CallKind) => {
      if (!user) {
        toast('Sign in required', 'Sign in to make calls.', 'destructive');
        return;
      }
      if (!peer?.id || peer.id === user.id) {
        toast('Cannot call yourself', 'Pick another person to call.');
        return;
      }
      if (sessionRef.current) {
        const existing = sessionRef.current;
        if (existing.status !== 'ended' && existing.status !== 'error') {
          toast('Already in a call', 'Finish the current call first.');
          return;
        }
        cleanup();
      }

      // Ask for the microphone/camera before the other side starts ringing. A
      // denied permission should not leave a phantom "missed call" in the chat.
      try {
        await prepare(kind, kind === 'video');
      } catch (error: any) {
        const message =
          error?.name === 'NotAllowedError'
            ? 'Microphone/camera permission was denied.'
            : error?.message || 'Could not access your microphone.';
        toast('Call failed', message, 'destructive');
        cleanup();
        applySession(null);
        return;
      }

      const { data: call, error } = await callService.createCall(peer.id, kind);
      if (error || !call) {
        toast(
          'Could not start call',
          error?.message || 'Please try again.',
          'destructive',
        );
        cleanup();
        applySession(null);
        return;
      }

      // Send push notification to the callee so they are alerted even if
      // the website is not open. Never blocks the call flow if it fails.
      const callerName = user?.user_metadata
        ? user.user_metadata.display_name ||
          user.user_metadata.user_name
        : "Someone";
      try {
        await messageService.sendPushNotification(
          peer.id,
          "Incoming call",
          `${callerName} is calling you`,
          { callId: call.id, kind, type: "incoming_call" },
        );
      } catch {
        // Push notification failed silently; call still proceeds.
      }

      applySession({
        id: call.id,
        kind,
        direction: 'outgoing',
        peer,
        status: 'outgoing',
        startedAt: null,
      });
      setupChannel(call.id);

      // The callee may have accepted while we were acquiring media.
      await maybeSendOffer();

      ringTimeoutRef.current = window.setTimeout(async () => {
        const current = sessionRef.current;
        if (!current || current.id !== call.id || current.status !== 'outgoing') return;
        await callService.updateStatus(call.id, 'missed');
        finishCall('ended');
        toast(
          'No answer',
          `${peer.display_name || peer.username || 'The user'} did not answer.`,
        );
      }, RING_TIMEOUT_MS);
    },
    [
      applySession,
      cleanup,
      finishCall,
      maybeSendOffer,
      prepare,
      setupChannel,
      toast,
      user,
    ],
  );

  const acceptCall = useCallback(async () => {
    const current = sessionRef.current;
    if (!current || current.status !== 'incoming' || !incomingRef.current) return;

    if (ringTimeoutRef.current) {
      window.clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    patchSession({ status: 'connecting' });

    try {
      // Acquire media before accepting so the caller's offer can be answered.
      await prepare(current.kind, current.kind === 'video');
      await callService.updateStatus(current.id, 'accepted');
      setShowFullScreen(true);
    } catch (error: any) {
      const message =
        error?.name === 'NotAllowedError'
          ? 'Microphone/camera permission was denied.'
          : error?.message || 'Could not access your microphone.';
      await callService.updateStatus(current.id, 'declined');
      toast('Call failed', message, 'destructive');
      cleanup();
      applySession(null);
    }
  }, [applySession, cleanup, patchSession, prepare, setShowFullScreen, toast]);

  const declineCall = useCallback(async () => {
    const current = sessionRef.current;
    if (!current) return;
    await callService.updateStatus(
      current.id,
      current.status === 'incoming' ? 'declined' : 'ended',
    );
    cleanup();
    applySession(null);
  }, [applySession, cleanup]);

  const endCall = useCallback(async () => {
    const current = sessionRef.current;
    if (!current) return;
    await callService.updateStatus(current.id, 'ended');
    finishCall('ended');
  }, [finishCall]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const tracks = stream.getAudioTracks();
    if (!tracks.length) return;
    const wasEnabled = tracks.some((track) => track.enabled);
    tracks.forEach((track) => {
      track.enabled = !wasEnabled;
    });
    setMuted(wasEnabled);
  }, []);

  const toggleCamera = useCallback(async () => {
    const stream = localStreamRef.current;
    if (!stream) return;

    if (!cameraOff) {
      // Turning camera off: just disable the video track.
      stream.getVideoTracks().forEach((track) => (track.enabled = false));
      setCameraOff(true);
      return;
    }

    // Turning camera on: request video access if needed. This triggers the
    // browser permission prompt if it was not already granted.
    try {
      let videoTrack = stream.getVideoTracks()[0];

      if (!videoTrack) {
        // This call started with audio only (or camera access failed).
        let videoStream: MediaStream;
        try {
          videoStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: cameraDevice || 'user' },
          });
        } catch {
          // Unsupported facing mode: accept any camera.
          videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        }

        videoTrack = videoStream.getVideoTracks()[0];
        stream.addTrack(videoTrack);
        // New MediaStream instance so React re-renders the local preview.
        setLocalStream(new MediaStream(stream.getTracks()));

        const pc = peerConnectionRef.current;
        if (pc) {
          const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
          if (sender) {
            await sender.replaceTrack(videoTrack);
          } else {
            pc.addTrack(videoTrack, stream);
            await renegotiate();
          }
        }
      } else {
        videoTrack.enabled = true;
      }

      setCameraOff(false);
    } catch (error: any) {
      console.error('Camera error:', error);
      let message: string;
      if (error?.name === 'NotAllowedError') {
        message = 'Camera permission denied. Allow camera access in browser settings.';
      } else if (error?.name === 'NotFoundError') {
        message = 'No camera found on this device.';
      } else if (error?.name === 'NotReadableError') {
        message = 'Camera is in use by another application.';
      } else if (error?.name === 'OverconstrainedError') {
        message = 'Camera constraints not supported by this device.';
      } else {
        message = error?.message || 'Could not access camera.';
      }
      toast('Camera error', message, 'destructive');
    }
  }, [cameraOff, cameraDevice, renegotiate, toast]);

  const toggleCameraDevice = useCallback(async () => {
    const stream = localStreamRef.current;
    const pc = peerConnectionRef.current;
    if (!stream || !pc) return;

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      if (videoDevices.length < 2) return;

      const currentTrack = stream.getVideoTracks()[0];
      // Without a live track the labels/ids may be hidden; default to the first.
      const currentDeviceId = currentTrack?.getSettings().deviceId;
      const currentIndex = videoDevices.findIndex((d) => d.deviceId === currentDeviceId);
      const nextDevice =
        videoDevices[(currentIndex + 1) % videoDevices.length] || videoDevices[0];

      const nextStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: nextDevice.deviceId } },
      });
      const nextTrack = nextStream.getVideoTracks()[0];

      const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
      if (sender) {
        await sender.replaceTrack(nextTrack);
      } else {
        pc.addTrack(nextTrack, stream);
        await renegotiate();
      }

      if (currentTrack) {
        stream.removeTrack(currentTrack);
        currentTrack.stop();
      }
      stream.addTrack(nextTrack);
      setLocalStream(new MediaStream(stream.getTracks()));

      // Mirror only when the browser reports the active camera's facing mode.
      const facing = nextTrack.getSettings().facingMode;
      setCameraDevice(facing === 'environment' ? 'environment' : 'user');
    } catch (error: any) {
      toast('Camera error', error?.message || 'Could not switch cameras.', 'destructive');
    }
  }, [renegotiate, toast]);

  const handleIncoming = useCallback(
    async (call: any) => {
      if (!call?.id) return;

      const busy =
        sessionRef.current &&
        sessionRef.current.status !== 'ended' &&
        sessionRef.current.status !== 'error';
      if (busy) {
        await callService.updateStatus(call.id, 'declined');
        return;
      }
      if (sessionRef.current) {
        cleanup();
        applySession(null);
      }

      incomingRef.current = { id: call.id, kind: call.kind };
      setupChannel(call.id);

      const { data: caller } = await profileService.getProfile(call.caller_id);

      // The caller may have hung up while we fetched the profile.
      if (cancelledCallsRef.current.has(call.id)) {
        cancelledCallsRef.current.delete(call.id);
        if (incomingRef.current?.id === call.id) incomingRef.current = null;
        cleanup();
        return;
      }
      if (!incomingRef.current || incomingRef.current.id !== call.id) {
        cleanup();
        return;
      }

      applySession({
        id: call.id,
        kind: call.kind,
        direction: 'incoming',
        peer: caller || { id: call.caller_id, username: 'Unknown' },
        status: 'incoming',
        startedAt: null,
      });

      ringTimeoutRef.current = window.setTimeout(async () => {
        const current = sessionRef.current;
        if (!current || current.id !== call.id || current.status !== 'incoming') return;
        await callService.updateStatus(call.id, 'missed');
        finishCall('ended');
      }, RING_TIMEOUT_MS);
    },
    [applySession, cleanup, finishCall, setupChannel],
  );

  const handleCallUpdate = useCallback(
    async (call: any) => {
      if (!call?.id) return;
      const current = sessionRef.current;

      if (!current || current.id !== call.id) {
        if (call.status === 'ended' || call.status === 'missed' || call.status === 'declined') {
          cancelledCallsRef.current.add(call.id);
        }
        return;
      }
      if (current.status === 'ended' || current.status === 'error') return;

      if (call.status === 'accepted') {
        if (current.direction === 'outgoing') {
          acceptedRef.current = true;
          if (current.status === 'outgoing') patchSession({ status: 'connecting' });
          await maybeSendOffer();
        }
        return;
      }

      if (call.status === 'declined') {
        if (current.direction === 'outgoing') {
          toast(
            'Call declined',
            `${current.peer.display_name || current.peer.username || 'The user'} declined the call.`,
          );
        }
        finishCall('ended');
        return;
      }

      if (call.status === 'ended' || call.status === 'missed') {
        finishCall('ended');
      }
    },
    [finishCall, maybeSendOffer, patchSession, toast],
  );

  const handleIncomingRef = useRef<(call: any) => Promise<void>>(async () => {});
  const handleCallUpdateRef = useRef<(call: any) => Promise<void>>(async () => {});

  useEffect(() => {
    handleIncomingRef.current = handleIncoming;
  }, [handleIncoming]);

  useEffect(() => {
    handleCallUpdateRef.current = handleCallUpdate;
  }, [handleCallUpdate]);

  // Ring for calls addressed to this user and react to call state changes.
  useEffect(() => {
    if (!user?.id) return;

    const incomingChannel = supabase.channel(`calls-in-${user.id}`);
    incomingChannel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'calls',
        filter: `callee_id=eq.${user.id}`,
      },
      (payload: any) => {
        void handleIncomingRef.current(payload.new);
      },
    );
    incomingChannel.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'calls',
        filter: `callee_id=eq.${user.id}`,
      },
      (payload: any) => {
        void handleCallUpdateRef.current(payload.new);
      },
    );
    incomingChannel.subscribe();

    const outgoingChannel = supabase.channel(`calls-out-${user.id}`);
    outgoingChannel.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'calls',
        filter: `caller_id=eq.${user.id}`,
      },
      (payload: any) => {
        void handleCallUpdateRef.current(payload.new);
      },
    );
    outgoingChannel.subscribe();

    return () => {
      supabase.removeChannel(incomingChannel);
      supabase.removeChannel(outgoingChannel);
    };
  }, [user?.id]);

  // Keep the on-screen duration in sync while the call is active.
  useEffect(() => {
    if (session?.status !== 'active' || !session.startedAt) return;
    const startedAt = session.startedAt;
    const tick = () =>
      setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    elapsedTimerRef.current = window.setInterval(tick, 1000);
    return () => {
      if (elapsedTimerRef.current) {
        window.clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
    };
  }, [session?.status, session?.startedAt]);

  // Drop the call locally if the user signs out.
  useEffect(() => {
    if (!user && sessionRef.current) {
      cleanup();
      applySession(null);
    }
  }, [applySession, cleanup, user]);

  // Release the microphone/camera if the provider unmounts.
  useEffect(() => () => cleanup(), [cleanup]);

  return (
    <CallContext.Provider
      value={{
        startCall,
        acceptCall,
        declineCall,
        endCall,
        toggleMute,
        toggleCamera,
        toggleCameraDevice,
        session,
        showFullScreen,
        localStream,
        remoteStream,
        muted,
        cameraOff,
        cameraDevice,
        hasCamera,
        elapsed,
      }}
    >
      {children}
      {session && session.status === 'incoming' && !showFullScreen ? (
        <CallIncomingModal
          session={session}
          onAccept={acceptCall}
          onDecline={declineCall}
        />
      ) : (
        session && (
          <CallOverlay
            session={session}
            localStream={localStream}
            remoteStream={remoteStream}
            muted={muted}
            cameraOff={cameraOff}
            cameraDevice={cameraDevice}
            hasCamera={hasCamera}
            elapsed={elapsed}
            onAccept={acceptCall}
            onDecline={declineCall}
            onEnd={endCall}
            onToggleMute={toggleMute}
            onToggleCamera={toggleCamera}
            onToggleCameraDevice={toggleCameraDevice}
          />
        )
      )}
    </CallContext.Provider>
  );
}

export function useCall() {
  const context = useContext(CallContext);
  if (!context) throw new Error('useCall must be used within a CallProvider');
  return context;
}
